import * as React from 'react';

declare global {
  interface Window { google?: any }
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('gsi load failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('gsi load failed'));
    document.head.appendChild(s);
  });
}

/**
 * Official Google sign-in button (GIS ID-token flow). Renders nothing when
 * REACT_APP_GOOGLE_CLIENT_ID is unset or the script fails — pages must not
 * depend on it being present.
 */
export function GoogleSignInButton({
  onCredential,
  text = 'continue_with',
}: {
  onCredential: (credential: string) => void;
  /** Button label variant: 'signin_with' (login) | 'signup_with' (signup) | 'continue_with'. */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
}) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const divRef = React.useRef<HTMLDivElement | null>(null);
  const onCredentialRef = React.useRef(onCredential);
  onCredentialRef.current = onCredential;
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !divRef.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp: { credential?: string }) => {
            if (resp?.credential) onCredentialRef.current(resp.credential);
          },
        });
        window.google.accounts.id.renderButton(divRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text,
          width: 320,
          logo_alignment: 'center',
        });
      })
      .catch(() => setFailed(true));
    return () => { cancelled = true; };
  }, [clientId, text]);

  if (!clientId || failed) return null;
  return <div ref={divRef} className="flex justify-center" data-testid="google-signin" />;
}
